import { Injectable } from '@angular/core';
import type { ActualSitecoreRouteData } from '@core/jss-models';
import { getRenderingData } from '@core/jss-routing';
import type { State } from '@innogy/utils-state';
import { select, Store } from '@ngrx/store';
import { lastValueFrom, merge, of } from 'rxjs';
import { filter, first, map, withLatestFrom } from 'rxjs/operators';
import { PageTrackEnhancer, type PageInfo } from '@analytics/models';

import {
  getActiveMainCategory,
  getActiveSubCategory,
  getBlogTemplateIds,
} from '../+state/blog.selector';
import type { JssBlogBase } from '../knowledge-base.model';

const knowledgeBaseCategory = 'kennisbank';
const isKnowledgeBase = (
  routeData: ActualSitecoreRouteData,
  templateIds: string[]
) =>
  routeData.templateId
    ? templateIds.indexOf(routeData.templateId) !== -1
    : false;
const isNotKnowledgeBase = (
  routeData: ActualSitecoreRouteData,
  templateIds: string[]
) => !isKnowledgeBase(routeData, templateIds);
const normalizeCategoryName = (name: string) =>
  name.toLowerCase().split(' ').join('-');
const combineCategoryNames = (result: string, category: JssBlogBase) =>
  category.name ? `${result}:${normalizeCategoryName(category.name)}` : result;
const reduceCategoryNames = (result: string, category: JssBlogBase | null) =>
  category ? combineCategoryNames(result, category) : result;
const mergeCategoryNames = (
  mainCategory: JssBlogBase | null,
  subCategory: JssBlogBase | null
) => {
  return {
    category: [mainCategory, subCategory].reduce(
      reduceCategoryNames,
      knowledgeBaseCategory
    ),
  };
};

@Injectable()
export class KnowledgeBaseAnalyticsEnhancer extends PageTrackEnhancer {
  constructor(private readonly store$: Store<State>) {
    super();
  }

  enhance(currentPageInfo: PageInfo) {
    const pageInfo$ = of(currentPageInfo);
    const routeData$ = pageInfo$.pipe(
      withLatestFrom(
        this.store$.pipe(getRenderingData),
        this.store$.pipe(select(getBlogTemplateIds))
      )
    );
    const notKnowledgeBase$ = routeData$.pipe(
      filter(([_, routeData, templateIds]) =>
        isNotKnowledgeBase(routeData, templateIds)
      ),
      map(() => ({}))
    );

    const enhanceKnowledgeBasePageInfo$ = routeData$.pipe(
      filter(([_, routeData, templateIds]) =>
        isKnowledgeBase(routeData, templateIds)
      ),
      map(([pageInfo, _]) => pageInfo),
      withLatestFrom(
        this.store$.pipe(getActiveMainCategory),
        this.store$.pipe(getActiveSubCategory)
      ),
      map(([_, mainCategory, subCategory]) =>
        mergeCategoryNames(mainCategory, subCategory)
      )
    );

    return lastValueFrom(
      merge(notKnowledgeBase$, enhanceKnowledgeBasePageInfo$).pipe(first())
    );
  }
}
